<template>
  <v-dialog
    v-model="showDialog"
    :fullscreen="isFullscreen"
    :max-width="isFullscreen ? null : '900px'"
    persistent
  >
    <v-card class="document-viewer-card" :class="{ 'fullscreen': isFullscreen }">
      <v-toolbar
        :color="color"
        dark
        flat
        dense
      >
        <v-btn
          icon
          @click="closeViewer"
        >
          <v-icon>close</v-icon>
        </v-btn>

        <v-toolbar-title class="text-truncate">
          {{ documentTitle }}
        </v-toolbar-title>

        <v-spacer />

        <!-- Page navigation in toolbar -->
        <div v-if="totalPages > 0" class="d-flex align-center mr-2">
          <v-btn icon small :disabled="currentPage <= 1" @click="prevPage">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <div class="page-input-wrapper mx-1">
            <input
              type="number"
              :value="currentPage"
              @change="goToPageFromInput"
              @keyup.enter="goToPageFromInput"
              class="page-input"
              :min="1"
              :max="totalPages"
            />
            <span class="page-total">/ {{ totalPages }}</span>
          </div>
          <v-btn icon small :disabled="currentPage >= totalPages" @click="nextPage">
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </div>

        <!-- Zoom controls -->
        <v-btn icon small @click="zoomOut" :disabled="scale <= 0.5">
          <v-icon>remove</v-icon>
        </v-btn>
        <span class="mx-1 text-body-2">{{ Math.round(scale * 100) }}%</span>
        <v-btn icon small @click="zoomIn" :disabled="scale >= 3">
          <v-icon>add</v-icon>
        </v-btn>

        <v-btn
          icon
          @click="toggleFullscreen"
          class="ml-2"
        >
          <v-icon>{{ fullscreenIcon }}</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 pdf-card-content">
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular
            :color="color"
            indeterminate
          />
          <p class="mt-4">Chargement...</p>
        </div>

        <div v-else-if="error" class="text-center pa-8">
          <v-icon size="64" color="error" class="mb-4">
            error_outline
          </v-icon>
          <p>{{ error }}</p>
          <v-btn
            :color="color"
            @click="loadDocument"
            class="mt-4"
          >
            Réessayer
          </v-btn>
        </div>

        <div
          v-else
          class="pdf-container"
          ref="pdfContainer"
          @contextmenu.prevent
        >
          <!-- Floating navigation buttons -->
          <v-btn
            v-if="totalPages > 1 && currentPage > 1"
            fab
            small
            :color="color"
            class="nav-btn nav-btn--prev"
            @click="prevPage"
          >
            <v-icon>chevron_left</v-icon>
          </v-btn>

          <canvas
            ref="pdfCanvas"
            class="pdf-canvas"
            @contextmenu.prevent
          />

          <v-btn
            v-if="totalPages > 1 && currentPage < totalPages"
            fab
            small
            :color="color"
            class="nav-btn nav-btn--next"
            @click="nextPage"
          >
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </div>

        <!-- Progress bar -->
        <div v-if="totalPages > 1 && !loading" class="page-progress">
          <v-progress-linear
            :value="(currentPage / totalPages) * 100"
            :color="color"
            height="3"
          />
        </div>
      </v-card-text>

      <v-card-actions v-if="!isFullscreen" class="pa-4" :class="{'aja-actions': isThemeAja}" :style="isThemeAja ? {backgroundColor: color} : {}">
        <v-spacer />
        <v-btn
          text
          :class="{'white--text': isThemeAja}"
          @click="closeViewer"
        >
          Fermer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry'

// Configure worker from local node_modules
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

export default {
  name: 'DocumentViewer',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    document: {
      type: Object,
      default: null
    },
    color: {
      type: String,
      default: 'primary'
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      scale: 1.0,
      isFullscreen: false,
      renderTask: null
    }
  },

  computed: {
    ...mapGetters(['isThemeAja']),

    showDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },

    documentTitle() {
      if (this.document && this.document.title) {
        return this.document.title
      }
      return 'Document'
    },

    fullscreenIcon() {
      return this.isFullscreen ? 'fullscreen_exit' : 'fullscreen'
    }
  },

  watch: {
    showDialog(newVal) {
      if (newVal && this.document) {
        this.loadDocument()
        this.addKeyboardListeners()
      } else {
        this.cleanup()
        this.removeKeyboardListeners()
      }
    }
  },

  methods: {
    ...mapActions(['documentDownloadRequest']),

    async loadDocument() {
      if (!this.document) return

      this.loading = true
      this.error = null
      this.currentPage = 1
      this.scale = 1.0

      try {
        const response = await this.$axios.get('/api/documents/' + this.document.id + '/download', {
          responseType: 'arraybuffer'
        })

        const typedArray = new Uint8Array(response.data)

        const loadingTask = pdfjsLib.getDocument({
          data: typedArray
        })

        this.pdfDoc = await loadingTask.promise
        this.totalPages = this.pdfDoc.numPages

        // Set loading to false first so canvas is visible
        this.loading = false

        // Wait for DOM to update
        await this.$nextTick()

        await this.renderPage(this.currentPage)
      } catch (err) {
        console.error('Erreur lors du chargement du document:', err)
        this.error = 'Impossible de charger le document'
        this.loading = false
      }
    },

    async renderPage(pageNum) {
      if (!this.pdfDoc) return

      // Cancel any ongoing render
      if (this.renderTask) {
        this.renderTask.cancel()
        this.renderTask = null
      }

      try {
        const page = await this.pdfDoc.getPage(pageNum)
        const canvas = this.$refs.pdfCanvas
        const container = this.$refs.pdfContainer

        if (!canvas || !container) return

        const context = canvas.getContext('2d')

        // Use device pixel ratio for high-DPI displays (Retina)
        const pixelRatio = window.devicePixelRatio || 1

        // Calculate available space based on dialog size
        // In fullscreen: use window dimensions
        // In normal mode: use 90vh max-height minus toolbar (48px) and actions (52px)
        let availableWidth, availableHeight

        if (this.isFullscreen) {
          availableWidth = window.innerWidth - 40
          availableHeight = window.innerHeight - 48 - 40 // toolbar + padding
        } else {
          // Dialog is max 900px wide, max 90vh tall
          const dialogWidth = Math.min(900, window.innerWidth - 48)
          const dialogHeight = window.innerHeight * 0.9
          availableWidth = dialogWidth - 40 // padding
          availableHeight = dialogHeight - 48 - 52 - 40 // toolbar + actions + padding
        }

        const viewport = page.getViewport({ scale: 1 })

        // Use the smaller scale to ensure the page fits entirely
        const scaleToFitWidth = availableWidth / viewport.width
        const scaleToFitHeight = availableHeight / viewport.height
        const baseScale = Math.min(scaleToFitWidth, scaleToFitHeight)
        const actualScale = baseScale * this.scale

        const scaledViewport = page.getViewport({ scale: actualScale * pixelRatio })

        // Set canvas dimensions at higher resolution
        canvas.height = scaledViewport.height
        canvas.width = scaledViewport.width

        // Scale canvas back down with CSS for sharp display
        canvas.style.width = (scaledViewport.width / pixelRatio) + 'px'
        canvas.style.height = (scaledViewport.height / pixelRatio) + 'px'

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
          enableWebGL: false,
          renderInteractiveForms: false
        }

        this.renderTask = page.render(renderContext)
        await this.renderTask.promise
        this.renderTask = null

        // Handle internal links (annotations) - use CSS viewport (without pixelRatio)
        const annotations = await page.getAnnotations()
        const cssViewport = page.getViewport({ scale: actualScale })
        this.setupAnnotations(annotations, cssViewport)

      } catch (err) {
        if (err.name !== 'RenderingCancelledException') {
          console.error('Erreur lors du rendu de la page:', err)
        }
      }
    },

    setupAnnotations(annotations, viewport) {
      // Remove existing annotation layer
      const container = this.$refs.pdfContainer
      const existingLayer = container.querySelector('.annotation-layer')
      if (existingLayer) {
        existingLayer.remove()
      }

      // Filter to only internal links
      const linkAnnotations = annotations.filter(ann =>
        ann.subtype === 'Link' && ann.dest
      )

      if (linkAnnotations.length === 0) return

      // Create annotation layer
      const annotationLayer = window.document.createElement('div')
      annotationLayer.className = 'annotation-layer'
      annotationLayer.style.cssText = `
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: ${viewport.width}px;
        height: ${viewport.height}px;
        pointer-events: none;
      `

      linkAnnotations.forEach(ann => {
        const link = window.document.createElement('a')
        const rect = viewport.convertToViewportRectangle(ann.rect)

        // rect is [x1, y1, x2, y2] in viewport coordinates (Y already converted)
        const left = Math.min(rect[0], rect[2])
        const top = Math.min(rect[1], rect[3])
        const width = Math.abs(rect[2] - rect[0])
        const height = Math.abs(rect[3] - rect[1])

        link.style.cssText = `
          position: absolute;
          left: ${left}px;
          top: ${top}px;
          width: ${width}px;
          height: ${height}px;
          pointer-events: auto;
          cursor: pointer;
        `

        link.addEventListener('click', (e) => {
          e.preventDefault()
          this.handleInternalLink(ann.dest)
        })

        annotationLayer.appendChild(link)
      })

      container.appendChild(annotationLayer)
    },

    async handleInternalLink(dest) {
      if (!this.pdfDoc || !dest) return

      try {
        let pageIndex
        if (typeof dest === 'string') {
          const destArray = await this.pdfDoc.getDestination(dest)
          if (destArray) {
            const ref = destArray[0]
            pageIndex = await this.pdfDoc.getPageIndex(ref)
          }
        } else if (Array.isArray(dest)) {
          const ref = dest[0]
          pageIndex = await this.pdfDoc.getPageIndex(ref)
        }

        if (pageIndex !== undefined) {
          this.currentPage = pageIndex + 1
          await this.renderPage(this.currentPage)
        }
      } catch (err) {
        console.error('Error handling internal link:', err)
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.renderPage(this.currentPage)
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.renderPage(this.currentPage)
      }
    },

    goToPageFromInput(event) {
      const page = parseInt(event.target.value, 10)
      if (!isNaN(page) && page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page
        this.renderPage(this.currentPage)
      } else {
        // Reset to current page if invalid
        event.target.value = this.currentPage
      }
    },

    zoomIn() {
      if (this.scale < 3) {
        this.scale = Math.min(3, this.scale + 0.25)
        this.renderPage(this.currentPage)
      }
    },

    zoomOut() {
      if (this.scale > 0.5) {
        this.scale = Math.max(0.5, this.scale - 0.25)
        this.renderPage(this.currentPage)
      }
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      this.$nextTick(() => {
        this.renderPage(this.currentPage)
      })
    },

    closeViewer() {
      this.showDialog = false
    },

    handleKeydown(e) {
      if (!this.showDialog) return

      // Block print shortcuts
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's')) {
        e.preventDefault()
        return
      }

      // Navigation
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        this.prevPage()
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        this.nextPage()
      } else if (e.key === 'Escape') {
        this.closeViewer()
      } else if (e.key === '+' || e.key === '=') {
        this.zoomIn()
      } else if (e.key === '-') {
        this.zoomOut()
      }
    },

    addKeyboardListeners() {
      window.document.addEventListener('keydown', this.handleKeydown)
    },

    removeKeyboardListeners() {
      window.document.removeEventListener('keydown', this.handleKeydown)
    },

    cleanup() {
      if (this.renderTask) {
        this.renderTask.cancel()
        this.renderTask = null
      }
      if (this.pdfDoc) {
        this.pdfDoc.destroy()
        this.pdfDoc = null
      }
      this.currentPage = 1
      this.totalPages = 0
      this.loading = false
      this.error = null
      this.isFullscreen = false
    }
  },

  beforeDestroy() {
    this.cleanup()
    this.removeKeyboardListeners()
  }
}
</script>

<style scoped>
.document-viewer-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.document-viewer-card.fullscreen {
  height: 100vh;
  max-height: 100vh;
}

.document-viewer-card.fullscreen::v-deep .v-toolbar {
  flex: 0 0 48px !important;
  max-height: 48px !important;
  min-height: 48px !important;
}

.document-viewer-card.fullscreen::v-deep .v-toolbar__content {
  height: 48px !important;
}

.pdf-card-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.pdf-container {
  background: #525659;
  flex: 1;
  overflow: auto;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  user-select: none;
  -webkit-user-select: none;
}

.pdf-canvas {
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
}

.fullscreen .pdf-container {
  height: calc(100vh - 48px);
}

/* Hide scrollbar but keep functionality */
.pdf-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.pdf-container::-webkit-scrollbar-track {
  background: #3d3d3d;
}

.pdf-container::-webkit-scrollbar-thumb {
  background: #666;
  border-radius: 4px;
}

.pdf-container::-webkit-scrollbar-thumb:hover {
  background: #888;
}

/* Page input in toolbar */
.page-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  padding: 2px 8px;
}

.page-input {
  width: 40px;
  background: transparent;
  border: none;
  color: white;
  text-align: center;
  font-size: 14px;
  outline: none;
  appearance: textfield;
  -moz-appearance: textfield;
}

.page-input::-webkit-outer-spin-button,
.page-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.page-total {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin-left: 4px;
}

/* Floating navigation buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.nav-btn:hover {
  opacity: 1;
}

.nav-btn--prev {
  left: 16px;
}

.nav-btn--next {
  right: 16px;
}

/* Progress bar */
.page-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .nav-btn {
    display: none;
  }

  .page-input {
    width: 32px;
  }
}
</style>
